/**
 * @file initCorsSolution.js
 * @description Inicializador automático del sistema CORS avanzado para Nubelytics
 * @version 1.0.0
 * @author Nubelytics Engineering Team
 */

import axios from 'axios';
import CorsFetchInterceptor from './CorsFetchInterceptor';
import { initCorsSolution } from './index';
import corsProxyConfig from './CorsProxyConfig';

/**
 * Dominios con problemas CORS conocidos que deberían usar siempre el proxy
 * Esta lista se basa en el análisis de errores históricos
 * y puede ser ampliada dinámicamente en runtime
 */
const KNOWN_PROBLEMATIC_DOMAINS = [
  'api.mercadolibre.com',
  'api.mercadopago.com',
  'api-3t.sandbox.paypal.com',
  'api-m.sandbox.paypal.com',
  'api.sandbox.paypal.com',
  'api-3t.paypal.com',
  'api-m.paypal.com',
  'api.paypal.com'
];

/**
 * Inicializa el sistema CORS con dominios problemáticos conocidos
 * Esta función debe ser llamada en el punto de entrada de la aplicación
 * @returns {Object} Objeto con métodos para diagnóstico y administración CORS
 */
export function initializeCorsSolutionWithDefaults() {
  console.info('[CORS] Inicializando sistema CORS avanzado...');
  
  try {
    // 1. Añadir dominios problemáticos conocidos a la lista de forzado de proxy
    let domainsAdded = 0;
    for (const domain of KNOWN_PROBLEMATIC_DOMAINS) {
      if (corsProxyConfig.addDomainToForceList(domain)) {
        domainsAdded++;
      }
    }
    console.info(`[CORS] Añadidos ${domainsAdded} dominios problemáticos conocidos a la lista de proxy forzado`);
    
    // 2. Inicializar el sistema CORS con registro de interceptores
    const corsRegistry = initCorsSolution();
    
    // 3. Realizar verificaciones básicas
    if (typeof window !== 'undefined') {
      // Verificar si la función de proxy está funcionando
      const testUrl = 'https://api.example.com/test';
      const proxiedUrl = corsProxyConfig.generateProxyUrl(testUrl);
      
      if (!proxiedUrl || proxiedUrl === testUrl) {
        console.warn('[CORS] Advertencia: La generación de URL proxy podría no estar funcionando correctamente');
      }
    }
    
    console.info('[CORS] Sistema CORS avanzado inicializado correctamente');
    return corsRegistry;
  } catch (error) {
    console.error('[CORS] Error inicializando sistema CORS:', error);
    // En caso de error, retornar un objeto con funciones vacías para evitar errores
    return {
      registerInterceptor: () => null,
      unregisterInterceptor: () => false
    };
  }
}

/**
 * Dominios con problemas de CORS detectados en tiempo real
 * Esta función analiza el localStorage para buscar errores recientes
 * y añade automáticamente los dominios problemáticos a la lista de forzado
 * @returns {Array} Dominios añadidos a la lista
 */
export function autoDetectProblematicDomains() {
  const addedDomains = [];
  
  try {
    // Buscar en localStorage registros de errores CORS
    const corsErrors = localStorage.getItem('nubelytics_cors_errors');
    
    if (corsErrors) {
      const errors = JSON.parse(corsErrors);
      
      // Analizar errores recientes (última semana)
      const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      const recentErrors = errors.filter(err => err.timestamp > oneWeekAgo);
      
      // Agrupar por dominio
      const domainCounts = {};
      for (const error of recentErrors) {
        if (error.domain) {
          domainCounts[error.domain] = (domainCounts[error.domain] || 0) + 1;
        }
      }
      
      // Si un dominio tiene más de 3 errores, añadirlo a la lista de forzado
      for (const [domain, count] of Object.entries(domainCounts)) {
        if (count >= 3) {
          if (corsProxyConfig.addDomainToForceList(domain)) {
            addedDomains.push(domain);
          }
        }
      }
      
      if (addedDomains.length > 0) {
        console.info(`[CORS] Auto-añadidos ${addedDomains.length} dominios problemáticos a la lista de proxy: ${addedDomains.join(', ')}`);
      }
    }
  } catch (error) {
    console.error('[CORS] Error al auto-detectar dominios problemáticos:', error);
  }
  
  return addedDomains;
}

/**
 * Integra el sistema CORS en la aplicación React
 * Debe ser llamado durante el bootstrap de la aplicación
 */
export default function bootstrapCorsSolution() {
  // Inicializar con valores por defecto
  const corsRegistry = initializeCorsSolutionWithDefaults();
  
  // Registrar interceptor global para fetch (si existe)
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    try {
      const fetchInterceptor = new CorsFetchInterceptor();
      fetchInterceptor.register();
      // Exponer para depuración
      if (process.env.NODE_ENV === 'development') {
        window.__NUBELYTICS_FETCH_INTERCEPTOR__ = fetchInterceptor;
      }
    } catch (e) {
      console.error('[CORS] No se pudo registrar CorsFetchInterceptor:', e);
    }
  }
  
  // Auto-detectar dominios problemáticos basados en errores pasados
  setTimeout(() => {
    autoDetectProblematicDomains();
  }, 5000); // Esperar 5 segundos para no impactar el rendimiento del arranque
  
  return corsRegistry;
}
